import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useLeasingCompaniesApi } from "../../http/leasingCompanies/api";
import { UpsertLeasingCompany } from "../../http/leasingCompanies/types";
import LeasingCompanyDocuments from "../leasingCompanyDocuments/LeasingCompanyDocuments";
import Header from "../ui/Header";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import LeasingCompanyForm, { LeasingCompanyFormBody } from "./LeasingCompanyForm";

const EditLeasingCompany: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.leasingCompanies.edit} />
      <TabsProvider>
        <TabbedEditLeasingCompany />
      </TabsProvider>
    </>
  );
};

export default EditLeasingCompany;

const TabbedEditLeasingCompany: FC = () => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const leasingCompaniesApi = useLeasingCompaniesApi();

  const { id } = useParams<{ id: string }>();

  const leasingCompanySwr = leasingCompaniesApi.getAdminLeasingCompany(id!, aborter.current.signal);
  const [leasingCompany, isInitialized] = useDataInitializer(leasingCompanySwr.data, leasingCompanySwr.error);

  useEffect(() => {
    id && leasingCompanySwr.trigger();
  }, [id]);

  useEffect(() => {
    leasingCompanySwr.error && dispatch(showErrorNotification(i18n, leasingCompanySwr.error));
  }, [leasingCompanySwr.error]);

  const onSubmit = async (formData: LeasingCompanyFormBody): Promise<void> => {
    if (leasingCompany?.id) {
      const body: UpsertLeasingCompany = {
        name: formData.name,
        address: formData.address,
        email: formData.email,
        phoneNumber: formData.phoneNumber != null ? formData.phoneNumber : undefined,
        currentVersionNumber: leasingCompany.versionNumber,
        notes: formData.notes,
      };

      setIsSubmitting(true);
      try {
        await leasingCompaniesApi.updateAdminLeasingCompany(leasingCompany.id, body);
        dispatch(showSuccessNotification(i18n.t));
        leasingCompanySwr.trigger();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const tabs = [{ title: i18n.t.common.edit }, { title: i18n.t.common.documents }];

  const tabComponents: TabContent[] = [
    {
      body: (
        <LeasingCompanyForm
          onSubmit={onSubmit}
          isDisabled={isSubmitting || !leasingCompany?.id || !userSettings.permissions.leasingCompanies.update}
          isLoading={!isInitialized}
          leasingCompany={leasingCompany}
        />
      ),
      eager: true,
      withBorder: true,
    },
    { body: <LeasingCompanyDocuments setIsDragging={setIsDragging} />, withBorder: true },
  ];

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} isDragging={isDragging} />
    </>
  );
};
